import React, { useState } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { X, Check, Ban, Edit } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const JobOfferDetailHSModal = ({ isOpen, onClose, onOfferUpdated, offer }) => {
  const [loading, setLoading] = useState(false);
  const [showJustificationModal, setShowJustificationModal] = useState(false);
  const [justification, setJustification] = useState('');

  if (!isOpen || !offer) return null;

  const handleAction = async (action, statusToSet = null) => {
    if (action === 'a_modifier') {
      // Show justification modal first
      setShowJustificationModal(true);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (action === 'valider') {
        // For Horizon Solution offers, we need to provide a default taux_horaire
        // Since it's already a CDD with taux_horaire, use existing value or default to 15.0
        const tauxHoraire = offer.taux_horaire || 15.0;
        
        const response = await fetch(`${API}/job-offers/${offer.id}/accept?taux_horaire=${tauxHoraire}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          toast.success('Offre validée avec succès');
          onOfferUpdated();
        } else {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.detail || 'Erreur lors de la validation de l\'offre';
          toast.error(errorMessage);
        }
      } else if (action === 'refuser') {
        // Delete the offer
        const response = await fetch(`${API}/job-offers/${offer.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          toast.success('Offre supprimée avec succès');
          onOfferUpdated();
        } else {
          toast.error('Erreur lors de la suppression de l\'offre');
        }
      }
    } catch (error) {
      console.error('Error handling offer action:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleModifyWithJustification = async () => {
    if (!justification.trim()) {
      toast.error('Une justification est requise pour renvoyer en modification');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Update offer status to "a_modifier" with justification
      const response = await fetch(`${API}/job-offers/${offer.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...offer,
          status: 'a_modifier',
          justification_modification: justification.trim(),
          updated_at: new Date().toISOString()
        }),
      });

      if (response.ok) {
        toast.success('Offre renvoyée en modification avec justification');
        setShowJustificationModal(false);
        setJustification('');
        onOfferUpdated();
      } else {
        toast.error('Erreur lors du renvoi en modification');
      }
    } catch (error) {
      console.error('Error sending offer for modification:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <>
      {/* Main Detail Modal */}
      <div className="job-offer-detail-modal-overlay">
        <div className="job-offer-detail-modal">
          <Card className="job-offer-detail-modal-card">
            <CardHeader className="job-offer-detail-modal-header">
              <div className="flex justify-between items-center">
                <CardTitle className="job-offer-detail-modal-title">
                  Détail de l'offre - Horizon Solution
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="job-offer-detail-modal-close"
                >
                  <X size={20} />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="job-offer-detail-modal-content">
              {/* Offer Details */}
              <div className="offer-detail-section">
                <h3 className="offer-detail-section-title">{offer.title}</h3>
                
                <div className="offer-detail-info-grid">
                  <div className="offer-detail-info-item">
                    <span className="offer-detail-label">Entreprise :</span>
                    <span>{offer.company}</span>
                  </div>
                  <div className="offer-detail-info-item">
                    <span className="offer-detail-label">Manager :</span>
                    <span>{offer.creator_name || 'Non spécifié'}</span>
                  </div>
                  <div className="offer-detail-info-item">
                    <span className="offer-detail-label">Date idéale d'arrivée :</span>
                    <span>{formatDate(offer.date_arrivee)}</span>
                  </div>
                  <div className="offer-detail-info-item">
                    <span className="offer-detail-label">Lieu de travail :</span>
                    <span>{offer.lieu_travail || 'Non spécifié'}</span>
                  </div>
                  {offer.type_poste && (
                    <div className="offer-detail-info-item">
                      <span className="offer-detail-label">Type de poste :</span>
                      <span>{offer.type_poste}</span>
                    </div>
                  )}
                  {offer.taux_horaire && (
                    <div className="offer-detail-info-item">
                      <span className="offer-detail-label">Taux horaire :</span>
                      <span>{offer.taux_horaire}€/h</span>
                    </div>
                  )}
                  <div className="offer-detail-info-item">
                    <span className="offer-detail-label">Créée le :</span>
                    <span>{formatDate(offer.created_at)}</span>
                  </div>
                </div>

                <div className="offer-detail-description">
                  <h4 className="offer-detail-subtitle">Description des missions</h4>
                  <p className="offer-detail-text">{offer.description}</p>
                </div>

                {offer.qualites_attendues && (
                  <div className="offer-detail-description">
                    <h4 className="offer-detail-subtitle">Qualités attendues</h4>
                    <p className="offer-detail-text">{offer.qualites_attendues}</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="offer-detail-actions">
                <Button
                  onClick={() => handleAction('valider')}
                  disabled={loading}
                  className="btn-success"
                  style={{ backgroundColor: '#22c55e', borderColor: '#22c55e' }}
                >
                  <Check size={16} className="mr-2" />
                  Valider
                </Button>
                <Button
                  onClick={() => handleAction('refuser')}
                  disabled={loading}
                  className="btn-danger"
                  style={{ backgroundColor: '#ef4444', borderColor: '#ef4444' }}
                >
                  <Ban size={16} className="mr-2" />
                  Refusé
                </Button>
                <Button
                  onClick={() => handleAction('a_modifier')}
                  disabled={loading}
                  variant="outline"
                  className="btn-warning"
                  style={{ borderColor: '#f59e0b', color: '#f59e0b' }}
                >
                  <Edit size={16} className="mr-2" />
                  À modifier
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Justification Modal */}
      {showJustificationModal && (
        <div className="justification-modal-overlay">
          <div className="justification-modal">
            <Card className="justification-modal-card">
              <CardHeader className="justification-modal-header">
                <div className="flex justify-between items-center">
                  <CardTitle className="justification-modal-title">
                    Justification de modification
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowJustificationModal(false)}
                    className="justification-modal-close"
                  >
                    <X size={20} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="justification-modal-content">
                <div className="form-group">
                  <Label htmlFor="justification" className="form-label">
                    Justification de la demande de modification *
                  </Label>
                  <Textarea
                    id="justification"
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                    className="form-textarea"
                    placeholder="Expliquez pourquoi cette offre doit être modifiée..."
                    rows={4}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Cette justification sera transmise au manager pour l'aider dans sa modification.
                  </p>
                </div>

                <div className="justification-actions">
                  <Button
                    onClick={handleModifyWithJustification}
                    disabled={loading || !justification.trim()}
                    className="btn-warning"
                    style={{ backgroundColor: '#f59e0b', borderColor: '#f59e0b' }}
                  >
                    <Edit size={16} className="mr-2" />
                    Renvoyer en modification
                  </Button>
                  <Button
                    onClick={() => setShowJustificationModal(false)}
                    disabled={loading}
                    variant="outline"
                    className="btn-cancel"
                  >
                    Annuler
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </>
  );
};

export default JobOfferDetailHSModal;